import { memo, useState, useEffect, useRef } from "react";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Grid2, Button, FormControl, FormLabel, TextField, Typography, Divider, FormHelperText } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useCommonStyles } from '../../styles/GenericStyle';
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { greyDark, red } from '../../colors';
import { useBeginProcessing, useMarkAsDone, useSkipStep } from '../../api/vegApl/Technician';
import { useGlobalState } from "../../contexts/GlobalState";
import { useSnackbar } from '../../contexts/SnackbarContext';
import FullScreenActionLoader from '../common/FullScreenActionLoader';

export type SpLotStatusPopModel = {

    openAddEditMatModal: boolean,
    type: string,
    setIsOpenCommentPopop: (val: boolean) => void,
    drillDownParam: any,
    SPLotWorkflowSteps: any,
    setIsRefreshDrillDown: (val: boolean) => void,
}
function SPLotProcessingSteps({ openAddEditMatModal, type, setIsOpenCommentPopop, drillDownParam, SPLotWorkflowSteps, setIsRefreshDrillDown }: SpLotStatusPopModel & { type: string }) {
    const classes = useCommonStyles();
    const { state: globalState, state } = useGlobalState();
    const [processForm, setProcessForm] = useState({
        comment: "",
        resourceId: ""
    });
    const [errorMessage, setErrorMessage] = useState({
        commentMsg: "",
        resourceMsg: ""
    });
    const { showSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const { roleSelected = "" } = state;
    const resourceIdInputRef = useRef<any>(null);
    const normalizeStepName = (stepName?: string) => String(stepName ?? '').trim().toLowerCase();
    const isScannedTreatmentStep = (stepName?: string) => {
        const normalized = normalizeStepName(stepName);
        if (normalized.includes('ferment')) return true;
        return /disinfection[^0-9]*([1-3])\b/.test(normalized);
    };
    const useScannedTreatmentText = isScannedTreatmentStep(SPLotWorkflowSteps?.current_protocol_step_name);
    const resourceFieldLabel = useScannedTreatmentText ? 'Scan Treatment' : 'Equipment ID';
    const resourceFieldPlaceholder = useScannedTreatmentText ? 'Enter or scan treatment' : 'Enter or Scan Equipment ID';

    const getTitle = (type: string) => {
        if (type == "skip") {
            return "Add Comment - Skip Step";
        }
        else if (type == "beginProcess") {
            return useScannedTreatmentText ? "Scan Treatment" : "Scan Equipment ID";
        }
        else if (type == "markAsDone") {
            return "Add Comment - Mark as Done";
        }
    }
    const { mutate: beginProcessing } = useBeginProcessing({
        mutation: {
            onSuccess: (data: any) => {
                successCall(data?.data?.message);
            },
            onError: (error: any) => {
                errorCall(error);
            },
        },
    })
    const { mutate: skipStep } = useSkipStep({
        mutation: {
            onSuccess: (data: any) => {
                successCall(data?.data?.message);
            },
            onError: (error: any) => {
                errorCall(error);
            },
        },
    })

    const { mutate: markAsDone } = useMarkAsDone({
        mutation: {
            onSuccess: (data: any) => {
                successCall(data?.data?.message);
            },
            onError: (error: any) => {
                errorCall(error);
            },
        },
    })

    const successCall = (_: string) => {
        //showSnackbar({ message: message, severity: 'success' });
        setIsLoading(false);
        setIsRefreshDrillDown(true);
        setIsOpenCommentPopop(false);

    }
    const errorCall = (error: any) => {
        const detail = error?.response?.data?.detail;
        const errorMessage = typeof detail === 'string' ? detail : 'Error in processing SPLot';
        showSnackbar({ message: errorMessage, severity: 'error' });
        setIsLoading(false);
    }

    const handleChange = (field: string, e: any) => {
        const value = e?.target?.value;
        setProcessForm((prev) => ({ ...prev, [field]: value }));
    };

    const processAction = () => {
        if (isLoading) return;
        let error: boolean = false;
        if (type == "skip" && !processForm.comment) {
            setErrorMessage((prev) => ({ ...prev, commentMsg: "Comment is required" }));
            error = true;
        }
        if (type == "beginProcess" && useScannedTreatmentText) {
            const scannedTreatment = processForm.resourceId.trim();
            const mappedTreatments: string[] = Array.isArray(SPLotWorkflowSteps?.mapped_treatments)
                ? SPLotWorkflowSteps.mapped_treatments
                : [];
            if (!scannedTreatment) {
                setErrorMessage((prev) => ({ ...prev, resourceMsg: 'Treatment is required' }));
                error = true;
            } else if (mappedTreatments.length > 0) {
                const isValid = mappedTreatments.some(
                    (t: string) => t.trim().toLowerCase() === scannedTreatment.toLowerCase()
                );
                if (!isValid) {
                    setErrorMessage((prev) => ({
                        ...prev,
                        resourceMsg: `Invalid treatment. Expected: ${mappedTreatments.join(', ')}`,
                    }));
                    setProcessForm((prev) => ({ ...prev, resourceId: '' }));
                    setTimeout(() => resourceIdInputRef.current?.focus(), 0);
                    error = true;
                }
            }
        }
        if (error) return;
        let req: any = {};
        req.comment = processForm.comment;
        req.splot_ids = [drillDownParam?.splot_id];
        req.user_id = globalState.user_id_db;
        req.comment = processForm.comment;
        req.workflow_step_id = SPLotWorkflowSteps?.current_protocol_step_id;
        req.workflow_protocol_id = SPLotWorkflowSteps?.current_workflow_protocol_id;
        if (type == "beginProcess") {
            setIsLoading(true);
            if (useScannedTreatmentText) {
                req.treatment_name = processForm.resourceId.trim();
            } else {
                req.equipment_id = processForm.resourceId;
            }
            beginProcessing({ data: req })
        }
        else if (type == "skip") {
            setIsLoading(true);
            skipStep({ data: req })
        }
        else if (type == "markAsDone") {
            setIsLoading(true);
            markAsDone({ data: req })
        }
    }

    useEffect(() => {
        setErrorMessage((prev) => ({ ...prev, commentMsg: "", resourceMsg: "" }));
        setProcessForm((prev) => ({ ...prev, "comment": "", resourceId: "" }));
    }, [openAddEditMatModal]);


    return (
        <>
            <FullScreenActionLoader open={isLoading} />
            <Dialog open={openAddEditMatModal} slotProps={{
                paper: {
                    sx: {
                        borderRadius: "16px",
                    },
                },
            }} sx={(theme) => ({
                ...MtComStyleObj(theme).drllDownCommentDialog
            })}>
                <DialogTitle sx={{ pb: 0, p: 3 }}>
                    <Box display={"flex"} justifyContent={"space-between"}>
                        <Typography component="div" className={classes.labelNoto20Bold600}>
                            {getTitle(type)}
                        </Typography>

                        <IconButton aria-label="close" onClick={() => setIsOpenCommentPopop(false)}
                            sx={(theme) => ({ ...MtComStyleObj(theme).dialogCloseButton })}><CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <Divider sx={(theme) => ({ ...MtComStyleObj(theme).dividerStyle, mt: 0 })} />
                <Box component="form" >
                    <DialogContent sx={{ pt: 1, pb: 1 }}>
                        {(type == "skip" || type == "markAsDone") && <Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
                            {type == "skip" && <FormControl size="small" fullWidth>
                                <FormLabel sx={{ color: greyDark, mt: 2 }} className={classes.labelInter14Bold400}>Current Step: <b>{SPLotWorkflowSteps?.current_protocol_step_name}</b></FormLabel>
                            </FormControl>}
                            {type == "markAsDone" && <Typography component="div" sx={{ mt: 2 }} className={classes.labelNoto18Bold600}>
                                Mark {SPLotWorkflowSteps?.current_protocol_step_name} step as done?
                            </Typography>}
                            <FormControl size="small" fullWidth sx={{ mt: 2 }}>
                                <FormLabel sx={{ color: greyDark }} className={classes.labelInter14Bold400}>Comment {type == "skip" && <span style={{ color: red, marginLeft: 4 }}>*</span>}</FormLabel>
                                <TextField
                                    sx={(theme) => ({
                                        mt: 1,
                                        ...MtComStyleObj(theme).textFocusHoverStyle,
                                        ...MtComStyleObj(theme).textBoxValueStyle,
                                    })}
                                    multiline
                                    disabled={["Breeder"].includes(roleSelected)}
                                    rows={6}
                                    onChange={(e) => handleChange("comment", e)}
                                    placeholder={type == "skip" ? "Enter reason for skipping" : "Enter comments"}
                                    fullWidth />
                                <FormHelperText sx={{ ml: 0, mt: 0 }} className={classes.errorText}>{errorMessage.commentMsg}</FormHelperText>

                            </FormControl>
                        </Grid2>}
                        <Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
                            {type == "beginProcess" && <FormControl size="small" fullWidth>
                                <FormLabel sx={{ color: "#0A0A0A", mt: 2 }} className={classes.labelNoto16Bold600}>{SPLotWorkflowSteps?.current_protocol_step_name}</FormLabel>
                            </FormControl>}
                            {type == "beginProcess" && <FormControl size="small" fullWidth sx={{ mt: 2 }}>
                                <FormLabel sx={{ color: "#404040" }} className={classes.labelInter14Bold400}>{resourceFieldLabel}</FormLabel>
                                <TextField
                                    inputRef={resourceIdInputRef}
                                    value={processForm.resourceId}
                                    sx={(theme) => ({

                                        mt: 1,
                                        ...MtComStyleObj(theme).textFocusHoverStyle,
                                        ...MtComStyleObj(theme).textBoxValueStyle,
                                        "& .MuiInputBase-root": {
                                            height: "40px",
                                        },

                                    })}
                                    placeholder={resourceFieldPlaceholder}
                                    onChange={(e) => {
                                        handleChange("resourceId", e);
                                        if (errorMessage.resourceMsg) setErrorMessage((prev) => ({ ...prev, resourceMsg: "" }));
                                    }}
                                    error={!!errorMessage.resourceMsg}
                                    fullWidth />
                                {errorMessage.resourceMsg && (
                                    <FormHelperText sx={{ ml: 0, mt: 0 }} className={classes.errorText}>{errorMessage.resourceMsg}</FormHelperText>
                                )}
                            </FormControl>
                            }
                        </Grid2>
                    </DialogContent>
                    <DialogActions sx={{ mb: 2, mt: 2 }}>
                        <Button variant="contained" disableRipple className={classes.whiteButton}
                            onClick={() => setIsOpenCommentPopop(false)} >Cancel</Button>
                        {type == "markAsDone" && <Button disableRipple variant="contained" className={classes.greenButton} loading={isLoading} loadingPosition="start"
                            onClick={processAction}>Confirm</Button>}
                        {type == "skip" && <Button disableRipple variant="contained" className={classes.redButton} loading={isLoading} loadingPosition="start"
                            onClick={processAction}>Skip Step</Button>}
                        {type == "beginProcess" && <Button disableRipple variant="contained" className={classes.greenButton}
                            loading={isLoading} loadingPosition="start" onClick={processAction}>Begin Processing</Button>}
                    </DialogActions>

                </Box>
            </Dialog >

        </>
    );
}
export default memo(SPLotProcessingSteps);